import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles } from 'lucide-react';

import { BACKEND_URL } from '../config';

// Use a separate localStorage key for bio prompt tracking
// This persists even after logout/login
const BIO_PROMPT_KEY = 'ophilia_bio_prompt_shown';

const BioPromptModal = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    checkShouldShowPrompt();
  }, []);

  const checkShouldShowPrompt = async () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    // Check if user is logged in
    if (!user.email) return;
    
    // Check if prompt was already shown (localStorage flag)
    const promptShownLocal = localStorage.getItem(BIO_PROMPT_KEY);
    if (promptShownLocal === 'true') return;
    
    // Check if user already has bio
    if (user.bio && user.bio.trim()) return;
    
    // Check if bio_prompt_shown is set in user object (from backend)
    if (user.bio_prompt_shown === true) {
      // Sync to localStorage for future checks
      localStorage.setItem(BIO_PROMPT_KEY, 'true');
      return;
    }
    
    // Double-check with backend if user has bio_prompt_shown set
    try {
      const response = await axios.get(`${BACKEND_URL}/api/users/check-bio-prompt?email=${encodeURIComponent(user.email)}`);
      if (response.data.bio_prompt_shown || response.data.has_bio) {
        localStorage.setItem(BIO_PROMPT_KEY, 'true');
        return;
      }
    } catch (error) {
      // If API fails, check localStorage flag as fallback
      console.error('Error checking bio prompt status:', error);
    }
    
    // Show modal after delay if all checks pass
    setTimeout(() => setShowModal(true), 1500);
  };

  const handleDismiss = async (navigateToProfile = false) => {
    setShowModal(false);
    
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    // Mark prompt as shown in localStorage immediately (most reliable)
    localStorage.setItem(BIO_PROMPT_KEY, 'true');
    
    // Also update user object in localStorage
    const updatedUser = { ...user, bio_prompt_shown: true };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    
    // Mark prompt as shown in backend
    try {
      await axios.post(`${BACKEND_URL}/api/users/bio-prompt-shown?email=${encodeURIComponent(user.email)}`);
    } catch (error) {
      console.error('Error marking bio prompt shown:', error);
    }
    
    if (navigateToProfile) {
      navigate('/profile');
    }
  };

  if (!showModal) return null;

  return (
    <Dialog open={showModal} onOpenChange={(open) => !open && handleDismiss(false)}>
      <DialogContent className="max-w-sm mx-auto">
        <DialogHeader>
          <div className="flex items-center justify-center mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-pink-100 rounded-full flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-orange-500" />
            </div>
          </div>
          <DialogTitle className="text-center text-xl">Complete Your Profile</DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-center text-stone-500">
            Add a short bio so other women can connect better.
          </p>
        </div>
        
        <div className="flex flex-col gap-3">
          <Button 
            onClick={() => handleDismiss(true)}
            className="w-full bg-orange-500 hover:from-orange-600 hover:to-pink-600"
          >
            Add Bio
          </Button>
          <Button 
            variant="ghost"
            onClick={() => handleDismiss(false)}
            className="w-full text-stone-500"
          >
            Maybe Later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BioPromptModal;
